/**
 * Lottie Header Logo Animation
 * Animates Lottie logo based on scroll position
 */

class LottieHeaderLogo {
  constructor(container) {
    this.lottieContainer = container;
    if (!this.lottieContainer) return;

    this.animation = null;
    this.scrollPosition = 0;
    // Get animation speed from data attribute, default to 300px
    this.maxScroll = parseInt(this.lottieContainer.dataset.animationSpeed) || 300;
    this.isInitialized = false;
    
    this.init();
  }

  init() {
    // Check if lottie is available
    if (typeof lottie === 'undefined') {
      console.error('Lottie library not loaded');
      return;
    }

    // Get animation path from data attribute
    const animationPath = this.lottieContainer.dataset.lottieUrl;
    if (!animationPath) {
      console.error('Lottie animation path not found');
      return;
    }

    // Load and configure Lottie animation
    this.animation = lottie.loadAnimation({
      container: this.lottieContainer,
      renderer: 'svg',
      loop: false,
      autoplay: false,
      path: animationPath,
      assetsPath: animationPath.substring(0, animationPath.lastIndexOf('/') + 1)
    });

    // Wait for animation to load
    this.animation.addEventListener('DOMLoaded', () => {
      this.isInitialized = true;
      this.totalFrames = this.animation.totalFrames;
      this.setupScrollListener();
      this.updateAnimationOnScroll(); // Initial call
    });

    // Error handling
    this.animation.addEventListener('data_failed', () => {
      console.error('Failed to load Lottie animation');
    });
  }

  setupScrollListener() {
    let ticking = false;

    const handleScroll = () => {
      this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.updateAnimationOnScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Also update on resize
    window.addEventListener('resize', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.updateAnimationOnScroll();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  updateAnimationOnScroll() {
    if (!this.isInitialized || !this.animation) return;

    // Calculate progress (0 to 1)
    const progress = Math.min(this.scrollPosition / this.maxScroll, 1);
    
    // Calculate frame based on progress
    const frame = Math.floor(progress * (this.totalFrames - 1));
    
    // Go to specific frame
    this.animation.goToAndStop(frame, true);
  }

  // Public method to update max scroll distance
  setMaxScroll(value) {
    this.maxScroll = value;
    this.updateAnimationOnScroll();
  }

  // Public method to destroy the instance
  destroy() {
    if (this.animation) {
      this.animation.destroy();
    }
  }
}

// Initialize all Lottie logo containers when DOM is ready
function initLottieLogos() {
  const containers = document.querySelectorAll('.lottie-logo-container');
  window.lottieHeaderLogos = [];
  
  containers.forEach(container => {
    // Skip if already initialized
    if (container.dataset.lottieInitialized) return;
    container.dataset.lottieInitialized = 'true';
    const instance = new LottieHeaderLogo(container);
    window.lottieHeaderLogos.push(instance);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initLottieLogos);
} else {
  initLottieLogos();
}

// Re-initialize on Shopify section re-render (theme editor)
document.addEventListener('shopify:section:load', initLottieLogos);
